import { PROJECT_CREATED } from "@/lib/analytics/amplitude-events";
import {
  getBrowserTimeCorrelationProps,
  resolveAmplitudeServerZoneFromEnv,
} from "@/lib/analytics/amplitude-time-correlation";
import { APP_ENV } from "@/lib/app-env";
import { getBuildVersion } from "@/lib/observability/build-info";
import { reportWarning } from "@/lib/observability/report-error";

const CONSENT_KEY = "vidseeds_cookie_consent";

// Lazy-load @amplitude/unified so Electron never pays parse cost.
type UnifiedModule = typeof import("@amplitude/unified");
let unified: UnifiedModule | null = null;

let initialized = false;
let replayActive = false;
const BUILD_VERSION = getBuildVersion();
let clientPlatform = "web";

const REPLAY_SAMPLE_RATE: Record<typeof APP_ENV, number> = {
  "dev-v2": 1.0,
  "stage-v2": 0.5,
  "prod-v2": 0.25,
};

const LOG_LEVEL: Record<typeof APP_ENV, number> = {
  "dev-v2": 1,
  "stage-v2": 1,
  "prod-v2": 0,
};

function isGuidesEngagementEnabled(): boolean {
  return process.env.NEXT_PUBLIC_AMPLITUDE_GUIDES_ENABLED === "true";
}

function isAnalyticsOptedOut(): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { choice: string };
    return parsed.choice === "rejected";
  } catch (err) {
    reportWarning(err, {
      source: "lib.analytics.amplitude",
      feature: "analytics",
      action: "isAnalyticsOptedOut",
    });
    return false;
  }
}

/** Sync Guides & Surveys opt-out with analytics cookie consent (best-effort). */
function syncEngagementConsent(accepted: boolean) {
  if (typeof window === "undefined") return;
  try {
    const engagement = window.engagement;
    if (!engagement) return;
    if (accepted) engagement.enable();
    else engagement.disable();
  } catch (err) {
    reportWarning(err, {
      source: "lib.analytics.amplitude",
      feature: "analytics",
      action: "syncEngagementConsent",
    });
  }
}

export type InitAmplitudeOptions = {
  /**
   * When true, skip the heavy autocapture features (formInteractions,
   * elementInteractions, networkTracking, webVitals, frustrationInteractions,
   * fileDownloads) at init time. Used on public marketing routes where full
   * DOM-event capture is unnecessary. Manual `trackEvent()` calls,
   * `sessions`, `attribution`, and the rest of the SDK still work.
   */
  lightweight?: boolean;
};

function buildFullAutocapture() {
  return {
    pageViews: false,
    sessions: true,
    formInteractions: true,
    fileDownloads: true,
    attribution: true,
    elementInteractions: true,
    frustrationInteractions: true,
    webVitals: true,
    networkTracking: {
      ignoreAmplitudeRequests: true,
      captureRules: [
        {
          urls: ["*"],
          statusCodeRange: "400-599",
          requestHeaders: ["content-type"],
          responseHeaders: ["content-type", "x-request-id"],
        },
      ],
    },
  };
}

function buildLightweightAutocapture() {
  return {
    pageViews: false,
    sessions: true,
    formInteractions: false,
    fileDownloads: false,
    attribution: true,
    elementInteractions: false,
    frustrationInteractions: false,
    webVitals: false,
    networkTracking: false,
  };
}

/** Initialize Amplitude Unified SDK (analytics + session replay + experiment + guides). Idempotent. */
export async function initAmplitude(opts: InitAmplitudeOptions = {}) {
  if (initialized || typeof window === "undefined") return;
  const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
  if (!apiKey) {
    console.warn("[Amplitude] NEXT_PUBLIC_AMPLITUDE_API_KEY is not set");
    return;
  }
  unified = await import("@amplitude/unified");
  const serverZone = resolveAmplitudeServerZoneFromEnv(process.env);
  const deploymentKey =
    process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY || undefined;

  const sessionReplayPrivacy = {
    maskSelector: [
      ".amp-mask",
      "[data-amp-mask]",
      'input[type="password"]',
      'input[type="email"]',
    ],
    blockSelector: [".amp-block", "[data-amp-block]"],
    unmaskSelector: [".amp-unmask"],
    defaultMaskLevel: "medium" as const,
  };

  const autocapture = opts.lightweight
    ? buildLightweightAutocapture()
    : buildFullAutocapture();

  // SENTRY FIX: NODE-2F4, NODE-2F6 — Amplitude init network failures
  // Date: 2026-04-14 | Agent: Claude Code
  // Root cause: unified.initAll() makes internal fetches to api.lab.amplitude.com.
  //   When these fail (ad blockers, ITP, network drops), the unhandled rejection
  //   bubbles to Sentry. Analytics init is non-critical and must degrade silently.
  // Fix: Wrap initAll in try/catch. Analytics failure must never block the app.
  try {
    await unified.initAll(apiKey, {
      serverZone,
      analytics: {
        appVersion: BUILD_VERSION,
        logLevel: LOG_LEVEL[APP_ENV],
        optOut: isAnalyticsOptedOut(),
        flushIntervalMillis: APP_ENV === "prod-v2" ? 1000 : 5000,
        autocapture,
      },
      // No capture for anonymous traffic; bump sample rate after login in startSessionReplay.
      sessionReplay: {
        sampleRate: 0,
        privacyConfig: sessionReplayPrivacy,
      },
      experiment: deploymentKey ? { deploymentKey } : {},
      engagement: {
        options: {
          headless: !isGuidesEngagementEnabled(),
          logLevel: LOG_LEVEL[APP_ENV],
        },
      },
    });
  } catch (err) {
    // Analytics is non-critical — degrade silently. The app works without it.
    console.warn(
      "[Amplitude] SDK initialization failed, continuing without analytics:",
      err instanceof Error ? err.message : String(err),
    );
    reportWarning(err, {
      source: "lib.analytics.amplitude",
      feature: "analytics",
      action: "initAll",
      severity: "warning",
    });
    return;
  }

  try {
    const tz = getBrowserTimeCorrelationProps();
    const identifyEvent = new unified.Identify();
    identifyEvent.set("iana_timezone", tz.iana_timezone);
    identifyEvent.set("js_timezone_offset_mins", tz.js_timezone_offset_mins);
    unified.identify(identifyEvent);
  } catch (err) {
    reportWarning(err, {
      source: "lib.analytics.amplitude",
      feature: "analytics",
      action: "identifyTimezoneContext",
    });
  }

  syncEngagementConsent(!isAnalyticsOptedOut());
  initialized = true;
}

/** True after `initAll` completes — identity/track calls before this risk Engagement race warnings. */
export function isAmplitudeInitialized(): boolean {
  return initialized;
}

export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
) {
  if (!unified || !initialized) return;
  const timeCtx =
    typeof window !== "undefined" ? getBrowserTimeCorrelationProps() : null;
  unified.track(eventName, {
    ...(timeCtx ?? {}),
    environment: APP_ENV,
    build_version: BUILD_VERSION,
    platform: clientPlatform,
    ...properties,
  });
}

export function trackProjectCreated(properties?: Record<string, unknown>) {
  trackEvent(PROJECT_CREATED, properties);
  incrementUserProperties({ total_projects_created: 1 });
}

export function trackRevenue(opts: {
  productId: string;
  price: number;
  quantity?: number;
  revenueType?: string;
  eventProperties?: Record<string, unknown>;
}) {
  if (!unified || !initialized) return;
  const rev = new unified.Revenue()
    .setProductId(opts.productId)
    .setPrice(opts.price)
    .setQuantity(opts.quantity ?? 1);
  if (opts.revenueType) rev.setRevenueType(opts.revenueType);
  if (opts.eventProperties)
    rev.setEventProperties(
      opts.eventProperties as { [key: string]: string | number | boolean },
    );
  unified.revenue(rev);
}

export function setAmplitudePlatformContext(ctx: {
  platform: string;
  os: string;
  browser: string;
  device_type: string;
}) {
  clientPlatform = ctx.platform;
  if (!unified) return;
  const identifyEvent = new unified.Identify();
  identifyEvent.set("platform", ctx.platform);
  identifyEvent.set("os", ctx.os);
  identifyEvent.set("browser", ctx.browser);
  identifyEvent.set("device_type", ctx.device_type);
  unified.identify(identifyEvent);
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  // SENTRY FIX: engagement race — unified is assigned after dynamic import but
  // initAll() may still be running; Engagement logs "not booted" if we identify early.
  if (!unified || !initialized) return;
  unified.setUserId(userId);
  if (traits) {
    const identifyEvent = new unified.Identify();
    Object.entries(traits).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        identifyEvent.set(key, value as string | number | boolean);
      }
    });
    unified.identify(identifyEvent);
  }
}

/**
 * Set immutable user properties (e.g. signup cohort) — safe across re-identify.
 */
export function identifyUserSetOnce(
  traits: Record<string, string | number | boolean>,
) {
  if (!unified || !initialized) return;
  const identifyEvent = new unified.Identify();
  Object.entries(traits).forEach(([key, value]) => {
    identifyEvent.setOnce(key, value);
  });
  unified.identify(identifyEvent);
}

export function identifyUserProperties(
  traits: Record<string, string | number | boolean>,
) {
  if (!unified || !initialized) return;
  const identifyEvent = new unified.Identify();
  Object.entries(traits).forEach(([key, value]) => {
    identifyEvent.set(key, value);
  });
  unified.identify(identifyEvent);
}

export function incrementUserProperties(traits: Record<string, number>) {
  if (!unified || !initialized) return;
  const identifyEvent = new unified.Identify();
  Object.entries(traits).forEach(([key, value]) => {
    identifyEvent.add(key, value);
  });
  unified.identify(identifyEvent);
}

export function setUserGroup(groupType: string, groupValue: string) {
  if (!unified || !initialized) return;
  unified.setGroup(groupType, groupValue);
}

export function resetAmplitudeUser() {
  if (!initialized) return;
  void stopSessionReplay();
  unified?.reset();
}

export function flushAmplitude() {
  unified?.flush();
}

/**
 * Refresh Experiment assignments for the current analytics user (call after identify).
 */
export async function refreshAmplitudeExperiments() {
  if (!unified || !initialized) return;
  try {
    const client = unified.experiment();
    await client?.fetch();
  } catch (err) {
    reportWarning(err, {
      source: "lib.analytics.amplitude",
      feature: "analytics",
      action: "refreshAmplitudeExperiments",
    });
  }
}

/**
 * Read a feature flag / experiment variant (exposure tracked by Experiment SDK when configured).
 */
export function getExperimentVariantValue(
  flagKey: string,
  fallback = "control",
): string {
  if (!unified || !initialized) return fallback;
  try {
    const client = unified.experiment();
    const v = client?.variant(flagKey, fallback);
    if (typeof v === "string") return v;
    if (v && typeof v === "object" && "value" in v && v.value != null) {
      return String(v.value);
    }
    return fallback;
  } catch (err) {
    reportWarning(err, {
      source: "lib.analytics.amplitude",
      feature: "analytics",
      action: "getExperimentVariantValue",
      extra: { flagKey },
    });
    return fallback;
  }
}

export async function startSessionReplay() {
  if (replayActive || !initialized || !unified || typeof window === "undefined")
    return;
  try {
    const { sessionReplayPlugin } = await import(
      "@amplitude/plugin-session-replay-browser"
    );
    await unified.remove("@amplitude/plugin-session-replay-browser").promise;
    const replay = sessionReplayPlugin({
      sampleRate: REPLAY_SAMPLE_RATE[APP_ENV],
      privacyConfig: {
        maskSelector: [
          ".amp-mask",
          "[data-amp-mask]",
          'input[type="password"]',
          'input[type="email"]',
        ],
        blockSelector: [".amp-block", "[data-amp-block]"],
        unmaskSelector: [".amp-unmask"],
        defaultMaskLevel: "medium",
      },
    });
    await unified.add(replay).promise;
    replayActive = true;
  } catch (err) {
    console.error("[Amplitude] Session replay init failed", err);
    reportWarning(err, {
      source: "lib.analytics.amplitude",
      feature: "analytics",
      action: "startSessionReplay",
    });
  }
}

export async function stopSessionReplay() {
  if (!replayActive || !unified) return;
  try {
    await unified.remove("@amplitude/plugin-session-replay-browser").promise;
    replayActive = false;
  } catch (err) {
    console.error("[Amplitude] Failed to remove session replay plugin", err);
    reportWarning(err, {
      source: "lib.analytics.amplitude",
      feature: "analytics",
      action: "stopSessionReplay",
    });
  }
}

export function setAmplitudeConsent(accepted: boolean) {
  if (!unified) return;
  unified.setOptOut(!accepted);
  syncEngagementConsent(accepted);
}

// Re-export catalog types for callers that import Identify/Revenue from this module.
export { Identify, Revenue } from "@amplitude/unified";
